import { QueryHookOptions, OperationVariables, useQuery } from '@apollo/client';
import { DeeplyReadonly, Query } from '@cubejs-client/core';
import { useCubeQuery, UseCubeQueryOptions } from '@cubejs-client/react';
import { DocumentNode } from 'graphql';
import { useMemo } from 'react';

interface ItemData {
  id: string;
  [propName: string]: any;
}

type DataNodesRoot = {
  nodes: ItemData[];
  totalCount: number | null;
};

export interface QueryOptions {
  getNodesRoot?: (data: any) => DataNodesRoot;
  mapNodes?: (node: ItemData) => ItemData;
  segmentMapper?: (value: any, index: number, array: any[]) => ItemData;
}
export interface UseGraphVariableQueryOptions<TData = any, TVariables extends OperationVariables = OperationVariables>
  extends QueryHookOptions<TData, TVariables>, QueryOptions {}

export interface UseCubeVariableQueryOptions extends UseCubeQueryOptions, QueryOptions {}

export interface UseVariableQueryResult {
  rows: ItemData[];
  loading: boolean;
  error: boolean;
}

export const useVariableGraphQuery = <TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  { mapNodes, getNodesRoot, ...options }: UseGraphVariableQueryOptions<TData, TVariables>,
): UseVariableQueryResult => {
  const { loading, error, data } = useQuery<TData, TVariables>(query, {
    ...options,
  });

  const rows: ItemData[] = useMemo(() => {
    if (data && getNodesRoot) {
      const { nodes } = getNodesRoot(data);
      return mapNodes ? nodes.map(mapNodes) : nodes;
    }
    return [];
  }, [data, getNodesRoot, mapNodes]);

  return {
    rows,
    loading,
    error: !!error,
  };
};

export const useVariableCubeQuery = (
  query: DeeplyReadonly<Query>,
  { segmentMapper: mapper, castNumerics }: UseCubeVariableQueryOptions,
): UseVariableQueryResult => {
  const { resultSet, isLoading, error } = useCubeQuery(query, { castNumerics });
  const segments = resultSet?.rawData();

  const rows: ItemData[] = useMemo(() => {
    if (segments && mapper) {
      return segments.map(mapper);
    }
    return [];
  }, [segments, mapper]);

  return {
    rows,
    loading: isLoading,
    error: !!error,
  };
};
